import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FADE_UP_30, FADE_UP_40, VISIBLE, VIEWPORT, TITLE_TRANSITION, EASE } from "@/lib/motion";

const ROLES = [
  {
    company: "Capital.com",
    role: "Lead — Performance Marketing",
    period: "2025 — Present",
    location: "Dubai, UAE",
    industry: "Fintech · CFDs · Trading · Crypto",
    variant: "featured",
    tags: ["Team leadership", "$5M / month", "40+ countries"],
  },
  {
    company: "Hantec Markets",
    role: "Manager — Paid Acquisition",
    period: "Nov 2024 — 2025",
    location: "Dubai, UAE",
    industry: "Fintech · Forex · CFDs",
    variant: "featured",
    tags: ["Team of 7", "80+ markets", "Server-side tracking"],
  },
  {
    company: "Deriv",
    role: "Specialist — Performance Marketing",
    period: "Oct 2022 — Nov 2024",
    location: "Dubai, UAE",
    industry: "Fintech · CFDs · Forex · Crypto",
    variant: "featured",
    tags: ["80+ markets", "$2.2M / quarter", "GA4 · Adjust · AppsFlyer"],
  },
  {
    company: "Plutus Centric",
    role: "Consultant — Acquisition",
    period: "Jun 2022 — Sep 2022",
    location: "Dubai, UAE",
    industry: "Fintech · Wealth Tech",
    variant: "compact",
    tags: ["Lead generation"],
  },
  {
    company: "Total IT Global",
    role: "Senior Specialist — Digital Marketing",
    period: "Apr 2021 — Jun 2022",
    location: "New Delhi, India",
    industry: "Enterprise IT · B2B SaaS",
    variant: "compact",
    tags: ["APAC · EMEA · AMER", "800% ROAS"],
  },
  {
    company: "Institute of Management Studies",
    role: "Manager — Digital Marketing",
    period: "Dec 2020 — Mar 2021",
    location: "Noida, India",
    industry: "Higher Education · EdTech",
    variant: "compact",
    tags: ["Google · Meta · LinkedIn PPC"],
  },
  {
    company: "EntNetwrk",
    role: "Manager — Digital Marketing",
    period: "Sep 2019 — Nov 2020",
    location: "Delhi, India",
    industry: "Consumer Tech · Mobile Apps",
    variant: "compact",
    tags: ["500% ROAS", "$0.024 CPI"],
  },
  {
    company: "Inventum GmbH",
    role: "Associate — Digital Marketing",
    period: "Aug 2018 — Jul 2019",
    location: "Warsaw, Poland",
    industry: "Tourism (MICE)",
    variant: "compact",
    tags: ["SEO · SEM · Display"],
  },
];

const TimelineItem = ({ role: r, index: idx, isLast }) => {
  const featured = r.variant === "featured";
  return (
    <motion.article
      initial={FADE_UP_40}
      whileInView={VISIBLE}
      viewport={VIEWPORT}
      transition={{ duration: 0.9, delay: Math.min(idx * 0.05, 0.4), ease: EASE }}
      className={`relative pl-12 md:pl-24 ${
        featured ? "py-10 md:py-14" : "py-6 md:py-8"
      } ${!isLast ? "border-b border-stroke" : ""}`}
      data-testid={`experience-item-${idx}`}
    >
      <div
        className={`absolute left-3 md:left-5 ${
          featured ? "top-11 md:top-16" : "top-8 md:top-10"
        } ${featured ? "w-3 h-3" : "w-2 h-2"} bg-obsidian border border-laser`}
      >
        {featured && <div className="absolute inset-[3px] bg-laser animate-pulse-dot" />}
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-8">
        <div className="col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-laser">
            {r.industry}
          </div>
          <div className={`mt-2 md:mt-3 font-mono text-ink ${featured ? "text-sm" : "text-xs md:text-sm"}`}>
            {r.period}
          </div>
          <div className="mt-1 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-steel">
            {r.location}
          </div>
        </div>

        <div className="col-span-12 md:col-span-9">
          <div className="flex items-baseline flex-wrap gap-x-4 gap-y-2">
            <h3
              className={`font-serif font-light tracking-tight text-ink leading-none ${
                featured
                  ? "text-3xl md:text-5xl lg:text-6xl"
                  : "text-2xl md:text-3xl lg:text-4xl"
              }`}
            >
              {r.company}
            </h3>
            <ArrowUpRight size={featured ? 22 : 18} strokeWidth={1} className="text-steel" />
          </div>
          <div className={`mt-2 md:mt-3 font-mono tracking-[0.2em] uppercase text-steel ${featured ? "text-xs md:text-sm" : "text-[11px] md:text-xs"}`}>
            {r.role}
          </div>

          <div className="mt-4 md:mt-6 flex flex-wrap gap-2">
            {r.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] tracking-[0.16em] uppercase text-ink/80 border border-stroke px-3 py-1.5 hover:border-laser/60 transition-colors"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default function Experience() {
  return (
    <section
      id="experience"
      className="relative container-x py-24 md:py-40"
      data-testid="experience-section"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24">
        <div className="col-span-12 md:col-span-4">
          <p className="eyebrow">Experience</p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <motion.h2
            initial={FADE_UP_30}
            whileInView={VISIBLE}
            viewport={VIEWPORT}
            transition={TITLE_TRANSITION}
            className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.96] tracking-tight text-ink"
            data-testid="experience-title"
          >
            A decade compounding paid acquisition.
          </motion.h2>
          <p className="mt-6 font-sans text-lg text-steel max-w-xl leading-relaxed">
            Since 2018. References and case studies on request.
          </p>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-4 md:left-6 top-2 bottom-2 w-px bg-laser/30" aria-hidden />
        <div>
          {ROLES.map((r, idx) => (
            <TimelineItem key={r.company + r.period} role={r} index={idx} isLast={idx === ROLES.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
